import { isEqual } from 'lodash-es';

type QueryObj = Record<string, string | number | boolean | null | undefined | (string | null)[]>;

/**
 * Parses the query obj by removing empty values and stringifying the rest.
 * Returns the new query if it is different from the old query, null otherwise.
 *
 * If preserveExisting is true, existing query params that are not present in the
 * new query will be preserved.
 */
export const parseNewQuery = ({
    oldQuery = {},
    newQuery = {},
    preserveExisting = false,
}: {
    oldQuery?: QueryObj;
    newQuery?: QueryObj;
    preserveExisting?: boolean;
}): Record<string, string> | null => {
    // If preserveExisting is true, spread oldQuery first then newQuery to override any changed values
    const queryToProcess = preserveExisting ? { ...oldQuery, ...newQuery } : newQuery;

    const parsedOldQuery = parseQuery(oldQuery);
    const parsedNewQuery = parseQuery(queryToProcess);

    // We should not update if the queries are identical.
    if (isEqual(parsedOldQuery, parsedNewQuery)) {
        return null;
    }

    return parsedNewQuery;
};

/**
 * Sort keys, filter out null/undefined values, convert values to strings.
 *
 * Note that in modern JS, the keys order will be preserved in an object.
 * Stringifying the values helps with comparing the objects, for example
 * { a: 1, b: 'test' } and { a: '1', b: 'test' } are considered equal.
 */
export const parseQuery = (query: QueryObj): Record<string, string> => {
    return Object.keys(query)
        .sort() // Sort keys alphabetically
        .reduce((result: Record<string, string>, key) => {
            const value = query[key];

            if (value !== null && value !== undefined && value !== '') {
                result[key] = String(value);
            }
            return result;
        }, {});
};
